import { useRef, useState, type KeyboardEvent } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Tooltip } from '@/components/ui/Tooltip';
import { Icon } from '@/components/ui/Icon';
import { filterTriggerHover } from '@/components/ui/filterTriggerStyles';
import { focusSurface } from '@/components/ui/focusStyles';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';

export interface CompactSortOption<T extends string> {
  value: T;
  label: string;
  icon: string;
  direction: 'up' | 'down';
}

const SortOptionIcon = ({ option }: { option: Pick<CompactSortOption<string>, 'icon' | 'direction'> }) => (
  <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center gap-0.5" aria-hidden="true">
    <Icon name={option.icon} size="custom" className="text-[14px]" ariaHidden />
    <Icon name={option.direction === 'down' ? ICONS.ARROW_DOWN : ICONS.ARROW_UP} size="custom" className="text-[8px]" ariaHidden />
  </span>
);

/** Compact sort action for toolbars: the selected order is exposed in the tooltip and menu. */
export function CompactSortMenu<T extends string>({
  value,
  options,
  onValueChange,
  label,
}: {
  value: T;
  options: readonly CompactSortOption<T>[];
  onValueChange: (value: T) => void;
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const selectedOptionRef = useRef<HTMLButtonElement>(null);
  const current = options.find((option) => option.value === value);
  const accessibleLabel = `${label}: ${current?.label ?? ''}`;

  const handleMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const buttons = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]'));
    const currentIndex = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const nextIndex = event.key === 'Home' ? 0
      : event.key === 'End' ? buttons.length - 1
        : event.key === 'ArrowDown' ? (currentIndex + 1) % buttons.length
          : (currentIndex - 1 + buttons.length) % buttons.length;
    buttons[nextIndex]?.focus();
  };

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <Tooltip content={accessibleLabel} side="bottom" open={!open && tooltipOpen} onOpenChange={setTooltipOpen}>
        <PopoverPrimitive.Trigger asChild>
          <button
            type="button"
            aria-label={accessibleLabel}
            className={`flex h-full w-full items-center justify-center rounded-lg border border-gray-200 bg-white leading-none text-gray-700 transition-colors duration-200 ${focusSurface} ${filterTriggerHover}`}
          >
            <span className="relative left-px flex items-center justify-center">{current && <SortOptionIcon option={current} />}</span>
          </button>
        </PopoverPrimitive.Trigger>
      </Tooltip>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="end"
          sideOffset={4}
          collisionPadding={12}
          className="z-popover-top max-h-[min(300px,calc(100vh-24px))] min-w-44 max-w-[calc(100vw-24px)] overflow-hidden rounded-lg border border-gray-200 bg-white p-1 shadow-lg"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            selectedOptionRef.current?.focus();
          }}
        >
          <div role="radiogroup" aria-label={label} onKeyDown={handleMenuKeyDown}>
            {options.map((option) => (
              <button
                key={option.value}
                ref={option.value === value ? selectedOptionRef : undefined}
                type="button"
                role="radio"
                aria-checked={option.value === value}
                onClick={() => {
                  onValueChange(option.value);
                  setOpen(false);
                }}
                className={cn(
                  'flex min-h-10 w-full cursor-pointer select-none items-center gap-2 rounded-md px-2.5 text-sm outline-none max-sm:min-h-11',
                  option.value === value
                    ? 'bg-primary-50 text-primary-800'
                    : 'text-gray-700 hover:bg-gray-50 focus:bg-gray-50'
                )}
              >
                <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center text-gray-600"><SortOptionIcon option={option} /></span>
                <span className="min-w-0 flex-1 truncate text-left">{option.label}</span>
                <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-primary-600">
                  {option.value === value && <Icon name={ICONS.CHECK} size="xs" ariaHidden />}
                </span>
              </button>
            ))}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
